import { faKey, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from 'providers/AuthProvider';
import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SignInForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const navigate = useNavigate();
  const { login, state } = useAuth();
  const { isLoading, error } = state;
  const [formData, setFormData] = useState({
    login: '',
    password: '',
    rememberMe: true
  });

  // Load saved credentials on component mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('rememberedCredentials');
    if (savedCredentials) {
      try {
        const { login, password, rememberMe } = JSON.parse(savedCredentials);
        setFormData({
          login: login || '',
          password: password || '',
          rememberMe: rememberMe !== false
        });
      } catch (error) {
        console.error('Error loading saved credentials:', error);
        localStorage.removeItem('rememberedCredentials');
      }
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.login || !formData.password) {
      return;
    }

    try {
      await login({
        username: formData.login,
        password: formData.password
      });

      // Save or clear credentials based on rememberMe checkbox
      if (formData.rememberMe) {
        localStorage.setItem(
          'rememberedCredentials',
          JSON.stringify({
            login: formData.login,
            password: formData.password,
            rememberMe: true
          })
        );
      } else {
        localStorage.removeItem('rememberedCredentials');
      }

      // Navigate to dashboard after successful login
      navigate('/app');
    } catch (error) {
      // Error is handled by the AuthProvider
      console.error('Login failed:', error);
    }
  };

  return (
    <>
      <div className="text-center mb-7">
        <h3 className="text-1000">Entrar</h3>
        <p className="text-700">Acesse a sua conta</p>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          <strong>Erro:</strong> {error}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="login">Email ou CPF</Form.Label>
          <div className="form-icon-container">
            <Form.Control
              id="login"
              name="login"
              type="text"
              className="form-icon-input"
              placeholder="seu@email.com ou 000.000.000-00"
              value={formData.login}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faUser}
              className="text-900 fs-9 form-icon"
            />
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="password">Senha</Form.Label>
          <div className="form-icon-container">
            <Form.Control
              id="password"
              name="password"
              type="password"
              className="form-icon-input"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon icon={faKey} className="text-900 fs-9 form-icon" />
          </div>
        </Form.Group>
        <Row className="flex-between-center mb-7">
          <Col xs="auto">
            <Form.Check type="checkbox" className="mb-0">
              <Form.Check.Input
                type="checkbox"
                name="rememberMe"
                id="remember-me"
                checked={formData.rememberMe}
                onChange={handleInputChange}
              />
              <Form.Check.Label htmlFor="remember-me" className="mb-0">
                Lembrar meus dados
              </Form.Check.Label>
            </Form.Check>
          </Col>
          <Col xs="auto">
            <Link to={`/forgot-password`} className="fs-9 fw-semi-bold">
              Esqueci a senha
            </Link>
          </Col>
        </Row>
        <Button
          type="submit"
          className="btn btn-sm btn-primary w-100 mb-3 fs-9 fw-bold"
          disabled={isLoading || !formData.login || !formData.password}
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Entrando...
            </>
          ) : (
            'Entrar'
          )}
        </Button>
      </Form>
      <div className="text-center">
        <Link to={`/sign-up`} className="fs-9 fw-bold">
          Criar uma conta
        </Link>
      </div>
    </>
  );
};

export default SignInForm;
